import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { CheckCircle2, Clock } from "lucide-react";
import { useAuthedGet, img, inr } from "@/lib/api";
import { useSeo } from "@/lib/store";

export const STATUS_LABEL = { pending_payment: "Awaiting payment", paid: "Paid", processing: "Processing", packed: "Packed", shipped: "Shipped", delivered: "Delivered", cancelled: "Cancelled", refunded: "Refunded" };

function Guard({ children }) {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  return isSignedIn ? children : <Navigate to="/sign-in" replace />;
}

function OrdersList() {
  const { data = [], isLoading } = useAuthedGet("/orders/mine");
  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-12" data-testid="orders-page">
      <h1 className="font-display text-5xl sm:text-6xl">ORDER HISTORY</h1>
      {isLoading ? <p className="mt-8 text-grey">Loading…</p> : !data.length ? (
        <div className="py-20 text-center" data-testid="orders-empty"><p className="font-display text-3xl">NO ORDERS YET.</p><Link to="/shop" className="btn-neon mt-6">Start shopping</Link></div>
      ) : (
        <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
          {data.map((o) => (
            <Link to={`/orders/${o.id}`} key={o.id} className="flex flex-wrap items-center gap-4 py-5 hover:bg-white/[0.02]" data-testid={`order-row-${o.order_no}`}>
              <div className="flex -space-x-4">{o.lines.slice(0, 3).map((l, i) => <img key={i} src={img(l.image)} alt="" className="w-14 h-16 object-cover border-2 border-[#080808]" />)}</div>
              <div className="flex-1"><p className="font-mono font-semibold">{o.order_no}</p><p className="text-xs text-grey">{new Date(o.created_at).toLocaleString()}</p></div>
              <span className={`font-mono text-xs px-2 py-1 ${o.status === "pending_payment" ? "bg-yellow-500/20 text-yellow-300" : "bg-neon text-black"}`}>{STATUS_LABEL[o.status]}</span>
              <span className="font-mono">{inr(o.total)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Detail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { data: o, isLoading } = useAuthedGet(`/orders/mine/${id}`);
  if (isLoading) return <p className="p-12 text-grey">Loading…</p>;
  if (!o) return <p className="p-12 font-display text-3xl">ORDER NOT FOUND</p>;
  const paid = o.status !== "pending_payment";
  return (
    <div className="max-w-[1000px] mx-auto px-4 sm:px-8 py-12" data-testid="order-detail-page">
      {sp.get("placed") && paid && (
        <div className="border border-neon p-8 mb-10 reveal" data-testid="order-confirmation">
          <CheckCircle2 className="w-12 h-12 text-neon" />
          <h1 className="font-display text-5xl mt-4">ORDER CONFIRMED.</h1>
          <p className="text-white/70 mt-2">Payment verified. We're packing your fit — you'll get updates on WhatsApp.</p>
        </div>
      )}
      {!paid && <div className="border border-yellow-500/50 p-5 mb-8 flex gap-3 text-sm" data-testid="order-pending-notice"><Clock className="w-5 h-5 text-yellow-400" />Payment not completed for this order. It will be confirmed only after verified payment.</div>}
      <div className="flex flex-wrap justify-between gap-4">
        <div><p className="eyebrow">Order</p><p className="font-display text-4xl" data-testid="order-number">{o.order_no}</p></div>
        <span className="font-mono text-sm self-end" data-testid="order-status">{STATUS_LABEL[o.status]}{o.tracking && ` · Tracking: ${o.tracking}`}</span>
      </div>
      <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
        {o.lines.map((l, i) => (
          <div key={i} className="flex gap-4 py-4"><img src={img(l.image)} alt="" className="w-16 h-20 object-cover" /><div className="flex-1"><p className="font-semibold">{l.title}</p><p className="font-mono text-xs text-grey">SIZE {l.size} × {l.qty}</p></div><p className="font-mono">{inr(l.line_total)}</p></div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-6 mt-8">
        <div className="text-sm text-white/70"><p className="label">Ship to</p>{o.address.name}<br />{o.address.line1} {o.address.line2}<br />{o.address.city}, {o.address.state} {o.address.pincode}<br />{o.address.phone}</div>
        <div className="font-mono text-sm space-y-1"><div className="flex justify-between"><span>Subtotal</span><span>{inr(o.subtotal)}</span></div><div className="flex justify-between text-neon"><span>Discount</span><span>-{inr(o.discount)}</span></div><div className="flex justify-between"><span>Shipping</span><span>{inr(o.shipping)}</span></div><div className="flex justify-between text-lg border-t border-white/10 pt-2"><span>Total</span><span>{inr(o.total)}</span></div></div>
      </div>
    </div>
  );
}

export function Orders() { useSeo("My Orders"); return <Guard><OrdersList /></Guard>; }
export function OrderDetail() { useSeo("Order"); return <Guard><Detail /></Guard>; }
